// Una columna «Todos + opciones» del alcance: el chip «Todos» arriba y las opciones debajo.
// La usan las secciones «A quién le aplica» para verse igual que Alcance.
import type { ReactNode } from 'react';
import { ScopeChip, ScopeChipGroup, type ScopeChipTone, type ScopeDim } from './ScopeChips';

export interface ScopeChoiceOption {
    value: string;
    label: string;
    selected: boolean;
    /** El color del dato, como el metal del nivel. */
    tone?: ScopeChipTone;
    /** El renglón chico bajo el nombre, como el conteo de un segmento. */
    detail?: string;
    /** Opción que todavía no se puede elegir: la leyenda va en el chip y el motivo en el tooltip. */
    blocked?: { legend: string; tooltip: string };
}

export interface ScopeChoiceGroupProps {
    dim: ScopeDim;
    title: string;
    help?: string;
    /** `single` se anuncia como radios; `multiple` como botones prendidos. */
    selection?: 'single' | 'multiple';
    allLabel: string;
    allSelected: boolean;
    onSelectAll: () => void;
    options: readonly ScopeChoiceOption[];
    onToggle: (value: string) => void;
    disabled?: boolean;
    /** Lo que se muestra cuando no hay opciones. */
    emptyLabel?: string;
    /** Aviso que va arriba de los chips, como un catálogo que no cargó. */
    notice?: ReactNode;
}

export function ScopeChoiceGroup({
    dim,
    title,
    help,
    selection = 'multiple',
    allLabel,
    allSelected,
    onSelectAll,
    options,
    onToggle,
    disabled,
    emptyLabel,
    notice,
}: ScopeChoiceGroupProps) {
    const single = selection === 'single';
    return (
        <ScopeChipGroup
            title={title}
            help={help}
            search={notice}
            listRole={single ? 'radiogroup' : undefined}
        >
            <ScopeChip
                asRadio={single}
                dim={dim}
                label={allLabel}
                selected={allSelected}
                disabled={disabled}
                onToggle={onSelectAll}
            />
            {options.map((option) => {
                // Una ya elegida sigue quitándose aunque hoy estuviera bloqueada.
                const blocked = option.blocked && !option.selected ? option.blocked : undefined;
                return (
                    <ScopeChip
                        key={option.value}
                        asRadio={single}
                        dim={dim}
                        label={option.label}
                        detail={blocked?.legend ?? option.detail}
                        tooltip={blocked?.tooltip}
                        tone={option.tone}
                        selected={option.selected}
                        disabled={disabled === true || blocked !== undefined}
                        onToggle={() => {
                            onToggle(option.value);
                        }}
                    />
                );
            })}
            {options.length === 0 && emptyLabel ? (
                <p className="text-[10.5px] text-muted-foreground">{emptyLabel}</p>
            ) : null}
        </ScopeChipGroup>
    );
}
